import React, { useState, useContext, useCallback, useMemo, memo } from 'react';
import styled from 'styled-components';

import Icon from '../../misc/Icon';
import Accordion from '../../misc/Accordion';
import TableEditorRte from './TableEditorRte';

import FileViews from '../../../constants/FileViews';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';
import CollectionEditor, { flattenSchemaTree } from './CollectionEditorv2';

const StyledAccordionContent = styled.div`
  display: relative;
  display: flex;
  flex-direction: row;
  flex: 1;
  padding: 8px;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 0px;
`;

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: gray8;
  flex: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width:${(props) => (props.view === FileViews.mobile ? '280px' : '480px')};
  color: ${(props) => props.theme.fontPrimaryColor || 'black'};
  svg {
    color: ${(props) => props.theme.fontPrimaryColor || 'black'} !important;
    font-size: 15px;
  }
`;

const StyledLabelIcon = styled.span`
  margin-right: 2px;
`;

const StyledDescription = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${(props) => props.theme.fontColorSecondary || 'gray'};
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(props) => props.theme.borderColor || 'gray'};
  border-radius: ${(props) => props.theme.borderRadius || 4}px;
  overflow: auto;
  padding: 8px;
  width: 100%;
`;

const StyledSchemaSection = styled.div`
  padding-left: ${(props) => props.depth * 24}px;
  margin-bottom: 16px;
  width: 100%;
`;

const StyledSchemaTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
`;

const generateRteSettingId = () => {
  const timestamp = ((new Date().getTime() / 1000) | 0).toString(16);
  return timestamp + 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16)).toLowerCase();
};

const SchemaRteEditorItem = memo(({
  schemaId, rteSettings, field,
  onSettingsCreate, onSettingsUpdate, onSettingsMoveLeft, onSettingsMoveRight, onSettingsDelete,
}) => {
  const activeFieldPosition = useMemo(() => ({ settings: rteSettings }), [rteSettings]);

  const handleCreate = useCallback(() => onSettingsCreate(schemaId), [schemaId, onSettingsCreate]);
  const handleUpdate = useCallback(
    (id, settingId, patch) => onSettingsUpdate(schemaId, settingId, patch),
    [schemaId, onSettingsUpdate],
  );
  const handleMoveLeft = useCallback(
    (id, settingId) => onSettingsMoveLeft(schemaId, settingId),
    [schemaId, onSettingsMoveLeft],
  );
  const handleMoveRight = useCallback(
    (id, settingId) => onSettingsMoveRight(schemaId, settingId),
    [schemaId, onSettingsMoveRight],
  );
  const handleDelete = useCallback(
    (id, settingId) => onSettingsDelete(schemaId, settingId),
    [schemaId, onSettingsDelete],
  );

  return (
    <TableEditorRte
      id={`${field}-${schemaId}`}
      fieldId={schemaId}
      activeFieldPosition={activeFieldPosition}
      onRteSettingsCreate={handleCreate}
      onRteSettingsUpdate={handleUpdate}
      onRteSettingsMoveLeft={handleMoveLeft}
      onRteSettingsMoveRight={handleMoveRight}
      onRteSettingsDelete={handleDelete}
    />
  );
});

const CollectionRteEditor = ({ schema, fieldPositionSchema, field, identifier, _id, onPositionChange }) => {

  const schemas = flattenSchemaTree(schema);

  const updateSchemaSettings = useCallback((schemaId, nextSettings) => {
    const data = fieldPositionSchema || {};
    const nextFieldPositionSchema = {
      ...data,
      [schemaId]: {
        ...(data[schemaId] || {}),
        settings: nextSettings,
      },
    };
    if (onPositionChange) onPositionChange(field, identifier, _id, { schema: nextFieldPositionSchema });
  }, [fieldPositionSchema, field, identifier, _id, onPositionChange]);

  const handleRteSettingsCreate = useCallback((schemaId) => {
    const currentSettings = fieldPositionSchema?.[schemaId]?.settings || [];
    const nextSettings = [...currentSettings, { _id: generateRteSettingId(), title: '', value: '' }];
    updateSchemaSettings(schemaId, nextSettings);
  }, [fieldPositionSchema, updateSchemaSettings]);

  const handleRteSettingsUpdate = useCallback((schemaId, settingId, patch) => {
    const currentSettings = fieldPositionSchema?.[schemaId]?.settings || [];
    const nextSettings = currentSettings.map((s) => s._id === settingId ? { ...s, ...patch } : s);
    updateSchemaSettings(schemaId, nextSettings);
  }, [fieldPositionSchema, updateSchemaSettings]);

  const handleRteSettingsMoveLeft = useCallback((schemaId, settingId) => {
    const currentSettings = fieldPositionSchema?.[schemaId]?.settings || [];
    const nextSettings = [...currentSettings];
    const index = nextSettings.findIndex((s) => s._id === settingId);
    if (index <= 0) return;
    const temp = nextSettings[index];
    nextSettings[index] = nextSettings[index - 1];
    nextSettings[index - 1] = temp;
    updateSchemaSettings(schemaId, nextSettings);
  }, [fieldPositionSchema, updateSchemaSettings]);

  const handleRteSettingsMoveRight = useCallback((schemaId, settingId) => {
    const currentSettings = fieldPositionSchema?.[schemaId]?.settings || [];
    const nextSettings = [...currentSettings];
    const index = nextSettings.findIndex((s) => s._id === settingId);
    if (index === -1 || index >= nextSettings.length - 1) return;
    const temp = nextSettings[index];
    nextSettings[index] = nextSettings[index + 1];
    nextSettings[index + 1] = temp;
    updateSchemaSettings(schemaId, nextSettings);
  }, [fieldPositionSchema, updateSchemaSettings]);

  const handleRteSettingsDelete = useCallback((schemaId, settingId) => {
    const currentSettings = fieldPositionSchema?.[schemaId]?.settings || [];
    const nextSettings = currentSettings.filter((s) => s._id !== settingId);
    updateSchemaSettings(schemaId, nextSettings);
  }, [fieldPositionSchema, updateSchemaSettings]);

  return (
    <>
      {schemas.map((s) => {
        const rteSettings = fieldPositionSchema?.[s.id]?.settings || [];
        return (
          <StyledSchemaSection key={s.id} depth={s.depth}>
            <StyledSchemaTitle>{s.title}</StyledSchemaTitle>
            <SchemaRteEditorItem
              schemaId={s.id}
              rteSettings={rteSettings}
              field={field}
              onSettingsCreate={handleRteSettingsCreate}
              onSettingsUpdate={handleRteSettingsUpdate}
              onSettingsMoveLeft={handleRteSettingsMoveLeft}
              onSettingsMoveRight={handleRteSettingsMoveRight}
              onSettingsDelete={handleRteSettingsDelete}
            />
          </StyledSchemaSection>
        );
      })}
    </>
  );

};

const CollectionEditorSettings = (props) => {

  const { activeFieldPosition, onChange, onPositionChange, onColumnEdit, onSchemaEdit, fieldSettings, features } = props;
  const { field, identifier, _id, schema, fieldPositionSchema, displayType } = activeFieldPosition;

  const isRte = displayType === FieldDisplayTypes.rte;

  const theme = useContext(ThemeContext);
  const [open, setOpen] = useState(true);

  return (
    <Accordion
      isOpen={open}
      height={400}
      enforceHeight
      onClick={() => setOpen(!open)}
      title={(
        <StyledHeader>
          <StyledLabel theme={theme}>
            <StyledLabelIcon><Icon icon={ThemeIcons.settings.settings} size="lg" /></StyledLabelIcon>
            {' '}
            {'Structure'}
          </StyledLabel>
          <StyledDescription theme={theme}>
            Structure view is what is used to build the schema portion of the field
          </StyledDescription>
        </StyledHeader>
      )}
    >
      <StyledAccordionContent>
        <StyledWrapper theme={theme}>
          {isRte ? (
            <CollectionRteEditor
              schema={schema}
              fieldPositionSchema={fieldPositionSchema}
              field={field}
              identifier={identifier}
              _id={_id}
              onPositionChange={onPositionChange}
            />
          ) : (
            <CollectionEditor
              schema={schema}
              fieldPositionSchema={fieldPositionSchema}
              rowDecorators={activeFieldPosition.rowDecorators}
              onChange={(schema) => onChange(field, identifier, _id, { schema })}
              onColumnEdit={(columnId, schemaId) => onColumnEdit(field, columnId, schemaId)}
              onSchemaEdit={(schemaId) => onSchemaEdit(schemaId)}
              fieldSettings={fieldSettings}
              features={features}
              fieldId={field}
            />
          )}
        </StyledWrapper>
      </StyledAccordionContent>
    </Accordion>
  );
};

export default CollectionEditorSettings;
